import { Alert, Button, Group, SimpleGrid, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconArrowLeft, IconPencil, IconPrinter } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { imeOsebe, naslov, vrsta as poKljucu } from './vrste';
import { KopirajOsebo } from './KopirajOsebo';
import { PovezaveOsebe } from './PovezaveOsebe';
import { IzpisPreizkusnje } from '../vozila/IzpisPreizkusnje';
import { DelavnicaOsebe } from '../delavnica/DelavnicaOsebe';
import { useDelavnica } from '../seja/paket';

type Oseba = Shema['OsebaPodatki'];
type Kontakt = Shema['OsebaPodrobnostiPodatki'];
type Obrazec = Oseba & Kontakt;

const OSNOVNI: [keyof Obrazec, string][] = [
  ['ime', 'Ime'],
  ['priimek', 'Priimek'],
  ['davcna', 'Davčna'],
  ['idZaDdv', 'ID za DDV'],
  ['emso', 'EMŠO'],
  ['maticnaSt', 'Mat. št.'],
  ['oib', 'OIB'],
  ['stDokumenta', 'Dokument'],
  ['trr', 'TRR'],
];
const NASLOV: [keyof Obrazec, string][] = [
  ['ulica', 'Ulica'],
  ['hisnaStevilka', 'Hišna št.'],
  ['postnaStevilka', 'Poštna št.'],
  ['kraj', 'Kraj'],
  ['drzava', 'Država'],
];
const KONTAKT: [keyof Obrazec, string][] = [
  ['tel1', 'Tel. 1'],
  ['tel2', 'Tel. 2'],
  ['tel3', 'Tel. 3'],
  ['tel4', 'Tel. 4'],
  ['faks', 'Faks'],
  ['email', 'E-pošta'],
  ['spletna', 'Spletna stran'],
];

/**
 * Podrobnosti osebe – kot OsebaOkno v Swingu: obstoječa se odpre za ogled, »Uredi« omogoči urejanje, »Shrani« shrani,
 * »Prekliči« vrne podatke; nova (pot …/nova) se odpre v urejanju z »Dodaj«. Napake strežnika se pokažejo ob polju.
 * Pod podatki obstoječe osebe sta seznama njenih dokumentov in vozil (PovezaveOsebe), pri kupcu z modulom Delavnica še
 * vozila v delavnici in delovni nalogi (DelavnicaOsebe).
 */
export function PodrobnostiOsebe() {
  const params = useParams();
  const v = poKljucu(params.vrsta);
  const nova = params.id === 'nova';
  const id = nova ? undefined : Number(params.id);

  const poizvedba = useQuery({
    queryKey: ['oseba', v.kljuc, id],
    enabled: !nova,
    queryFn: async ({ signal }) => {
      const path = { vrsta: v.kljuc, id: id! };
      const [oseba, kontakt] = await Promise.all([
        podatki(api.GET('/api/osebe/{vrsta}/{id}', { signal, params: { path } })),
        // 204 = oseba kontaktnih podatkov nima
        podatki(api.GET('/api/osebe/{vrsta}/{id}/podrobnosti', { signal, params: { path } })),
      ]);
      return { ...(kontakt ?? {}), ...oseba } as Obrazec;
    },
  });

  if (!nova && !poizvedba.data) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Osebe ni bilo mogoče naložiti"
      />
    );
  }
  // ob novih podatkih (po shranjevanju) se obrazec ponastavi
  return (
    <ObrazecOsebe
      key={`${v.kljuc}-${params.id}-${poizvedba.dataUpdatedAt}`}
      nova={nova}
      id={id}
      shranjena={nova ? {} : poizvedba.data!}
    />
  );
}

function ObrazecOsebe({ nova, id, shranjena }: { nova: boolean; id?: number; shranjena: Obrazec }) {
  const params = useParams();
  const v = poKljucu(params.vrsta);
  const navigate = useNavigate();
  const odjemalec = useQueryClient();
  const [urejanje, setUrejanje] = useState(nova);
  const [obrazec, setObrazec] = useState<Obrazec>(shranjena);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);
  const [preizkusnja, setPreizkusnja] = useState(false);
  const delavnica = useDelavnica();

  // brskalnik vpraša pred zaprtjem/osvežitvijo strani z neshranjenimi spremembami
  const spremenjeno = urejanje && JSON.stringify(obrazec) !== JSON.stringify(shranjena);
  useEffect(() => {
    if (!spremenjeno) {
      return;
    }
    const opozori = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', opozori);
    return () => window.removeEventListener('beforeunload', opozori);
  }, [spremenjeno]);

  const nastavi = (k: keyof Obrazec, vrednost: string) => setObrazec((o) => ({ ...o, [k]: vrednost }));

  const shrani = async () => {
    setNapaka(null);
    setPolja({});
    setShranjujem(true);
    try {
      const oseba: Oseba = {
        id: id ?? 0,
        ...Object.fromEntries([...OSNOVNI, ...NASLOV].map(([k]) => [k, obrazec[k] ?? ''])),
        kodaDrzave: obrazec.kodaDrzave,
      };
      const kontakt: Kontakt = Object.fromEntries(
        [...KONTAKT, ['zaznamki', ''] as const].map(([k]) => [k, (obrazec as Record<string, unknown>)[k] ?? '']),
      );
      const shranjenaOseba = nova
        ? await podatki(api.POST('/api/osebe/{vrsta}', { params: { path: { vrsta: v.kljuc } }, body: oseba }))
        : await podatki(
            api.PUT('/api/osebe/{vrsta}/{id}', { params: { path: { vrsta: v.kljuc, id: id! } }, body: oseba }),
          );
      await podatki(
        api.PUT('/api/osebe/{vrsta}/{id}/podrobnosti', {
          params: { path: { vrsta: v.kljuc, id: shranjenaOseba.id! } },
          body: kontakt,
        }),
      );
      await odjemalec.invalidateQueries({ queryKey: ['osebe'] });
      await odjemalec.invalidateQueries({ queryKey: ['oseba', v.kljuc, shranjenaOseba.id] });
      notifications.show({ color: 'green', message: nova ? `${v.ednina} je dodan.` : 'Spremembe so shranjene.' });
      if (nova) {
        void navigate(`/osebe/${v.kljuc}/${shranjenaOseba.id}`, { replace: true });
      } else {
        setUrejanje(false);
      }
    } catch (e) {
      // napaka polja se pokaže samo ob polju, ostale zgoraj
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setShranjujem(false);
    }
  };

  const preklici = () => {
    if (nova) {
      void navigate(`/osebe${v.kljuc === 'kupci' ? '' : `?vrsta=${v.kljuc}`}`);
      return;
    }
    setObrazec(shranjena);
    setPolja({});
    setNapaka(null);
    setUrejanje(false);
  };

  const vnos = ([k, oznaka]: [keyof Obrazec, string]) => (
    <TextInput
      key={k}
      label={oznaka}
      value={String(obrazec[k] ?? '')}
      onChange={(e) => nastavi(k, e.currentTarget.value)}
      error={polja[k]}
      withAsterisk={k === 'ime'}
      size="sm"
    />
  );
  const prikaz = (seznam: [keyof Obrazec, string][]): [string, ReactNode][] =>
    seznam.map(([k, oznaka]) => [oznaka, String(obrazec[k] ?? '')]);

  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to={`/osebe${v.kljuc === 'kupci' ? '' : `?vrsta=${v.kljuc}`}`}
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        {poKljucu(v.kljuc).naziv}
      </Button>
      <GlavaZaslona
        naslov={nova ? v.nova : imeOsebe(shranjena) || `${v.ednina} brez imena`}
        opis={nova || !imeOsebe(shranjena) ? undefined : v.ednina}
        dejanja={
          urejanje ? (
            <Group gap="xs">
              <Button variant="subtle" onClick={preklici} disabled={shranjujem}>
                Prekliči
              </Button>
              <Button onClick={() => void shrani()} loading={shranjujem}>
                {nova ? 'Dodaj' : 'Shrani'}
              </Button>
            </Group>
          ) : (
            <Group gap="xs">
              {v.kljuc === 'uporabniki-vozil' && (
                <Button variant="default" leftSection={<IconPrinter size={18} />} onClick={() => setPreizkusnja(true)}>
                  Preizkušnja
                </Button>
              )}
              {id !== undefined && <KopirajOsebo vrsta={v} id={id} />}
              <Button leftSection={<IconPencil size={18} />} onClick={() => setUrejanje(true)}>
                Uredi
              </Button>
            </Group>
          )
        }
      />
      {napaka && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} mb="md" role="alert">
          {napaka}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Stack gap="md">
          <Kartica naslov="Osnovni podatki">
            {urejanje ? (
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                {OSNOVNI.map(vnos)}
              </SimpleGrid>
            ) : (
              <Podatki vrstice={prikaz(OSNOVNI)} />
            )}
          </Kartica>
          <Kartica naslov="Naslov">
            {urejanje ? (
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                {NASLOV.map(vnos)}
              </SimpleGrid>
            ) : (
              <Podatki
                vrstice={[
                  ['Naslov', naslov(obrazec)],
                  ['Država', String(obrazec.drzava ?? '')],
                ]}
              />
            )}
          </Kartica>
        </Stack>
        <Stack gap="md">
          <Kartica naslov="Kontakt">
            {urejanje ? (
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                {KONTAKT.map(vnos)}
              </SimpleGrid>
            ) : (
              <Podatki vrstice={prikaz(KONTAKT)} />
            )}
          </Kartica>
          <Kartica naslov="Zaznamki">
            {urejanje ? (
              <Textarea
                aria-label="Zaznamki"
                value={String(obrazec.zaznamki ?? '')}
                onChange={(e) => nastavi('zaznamki', e.currentTarget.value)}
                autosize
                minRows={3}
                maxRows={10}
              />
            ) : (
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} c={obrazec.zaznamki ? undefined : 'dimmed'}>
                {obrazec.zaznamki || 'Brez zaznamkov.'}
              </Text>
            )}
          </Kartica>
        </Stack>
      </SimpleGrid>
      {id !== undefined && <PovezaveOsebe vrsta={v} id={id} />}
      {id !== undefined && v.kljuc === 'kupci' && delavnica && <DelavnicaOsebe kupecId={id} />}
      {v.kljuc === 'uporabniki-vozil' && !nova && (
        <IzpisPreizkusnje
          odprto={preizkusnja}
          zapri={() => setPreizkusnja(false)}
          uporabnik={{ ...shranjena, id: id! }}
        />
      )}
    </ZaslonSeznama>
  );
}
